import { asCurrency, getAmortization, getPrincipal, updateValuesFromFields, validateFields } from '../util'
import { useAppState } from '../Context'
import { BalanceInterestChart } from '../components/Charts'
import { AmortizationScheduleTable } from '../components/Tables'
import { InputFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import CalculateRow from '../components/CalculateRow'
import currency from 'currency.js'

function PanelInput() {
  return <InputFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  return (
    <CalculateResults>
      <CalculateRow>
        <div>This Monthly Payment Can Fund a Purchase of:</div>
        <div className="font-bold">{asCurrency(values.totalAfford)}</div>
      </CalculateRow>

      <CalculateRow className="font-bold bg-gray-100">
        <div className="w-full text-center">Your Monthly Payment Would Be Broken Down As Follows:</div>
      </CalculateRow>

      <CalculateRow>
        <div>Payment Amount:</div>
        <div>{asCurrency(values.payment)}</div>
      </CalculateRow>

      <CalculateRow>
        <div>Insurance and Other Costs:</div>
        <div>{asCurrency(currency(values.monthlyInsuranceCosts).add(values.otherMonthlyCosts))}</div>
      </CalculateRow>

      <CalculateRow className="font-bold">
        <div>Total:</div>
        <div>{asCurrency(values.highestPaymentAfford)}</div>
      </CalculateRow>

      <CalculateRow>
        <div>
          <strong>Note:</strong> This calculator tells you how large your loan will be, but will not tell you whether
          you would qualify for the loan.
        </div>
      </CalculateRow>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <BalanceInterestChart amortization={values.amortization} title="Car Affordability Calculator" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return <AmortizationScheduleTable amortization={values.amortization} />
}

export default {
  name: 'Car Affordability Calculator',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)
          const payment =
            updatedValues.highestPaymentAfford - updatedValues.monthlyInsuranceCosts - updatedValues.otherMonthlyCosts
          const principal = getPrincipal(payment, updatedValues.interestRate, updatedValues.numPayments)
          const amortization = getAmortization(principal, updatedValues.interestRate, updatedValues.numPayments)
          const totalAfford = currency(principal).add(updatedValues.downPayment)

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'payment', value: payment } })
          dispatch({ type: 'setValue', payload: { name: 'principal', value: principal } })
          dispatch({ type: 'setValue', payload: { name: 'totalAfford', value: totalAfford } })
          dispatch({ type: 'setValue', payload: { name: 'amortization', value: amortization } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'highestPaymentAfford',
      label: 'Highest Payment Amount You Can Afford',
      value: 500,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'monthlyInsuranceCosts',
      label: 'Monthly Insurance Costs',
      value: 100,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'otherMonthlyCosts',
      label: 'Other Monthly Costs (Parking, Gas, Stickers)',
      value: 40,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'downPayment',
      label: 'Down Payment Amount',
      value: 1000,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'numPayments',
      label: 'Loan Term (Months)',
      value: 60,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate',
      label: 'Interest Rate',
      value: 9.25,
    },
  ],
}
